// The play card (ui-qol §4.1): a white 325 × 302 card in the centre column —
// - gear (settings) top-left and our "Source" icon link top-right, 24 px #7D7D7D at 0.8 opacity;
// - the ball.io wordmark, ≈ 47 px tall, 40 px from the top;
// - the 180 × 28 nick field (2 px #A2A2A2 border, 16 px Arial #343434, placeholder "Nickname",
//   Enter = Play) and the 243 × 34 green Play button; the label is "Play" in every state (also
//   on the Esc menu, where it resumes the same life);
// - two instruction lines in bold 12.3 px #7D7D7D (touch devices get the touch lines);
// - a white "Connecting…" blocker with a grey ring spinner over the whole card while there is no
//   room yet; while the server answers `full` (every room taken, or its capacity guard refusing,
//   architecture §6.3) the same blocker reads "Servers are full" with "Retrying in N s…" under
//   it, counting down to the Connector's next ask (`blockerView`).
// - "N players online" (`/api/info.online`: real people on this server, bots excluded) in the
//   same grey bold 12 px, pinned to the card's bottom edge, so it neither moves the card's
//   content nor changes its height when it appears, updates or hides (mobile too).
// Money rooms add one line under Play: "Buy-in $1 · you start with $0.90" with a TEST MONEY tag
// while the server runs the fake settlement.
//
// The skin button and its "Draw your own!" bubble wait for the skin picker (not built yet), so the
// play row is centred without it.
//
// Consumer: app.ts (via ui/menu/layout.ts).

import { NET_TIMING } from '@ballio/shared';
import type { AssetImage } from '../../assets/loader.js';
import type { UiClickSource } from '../../events/bus.js';
import type { StorageLike } from '../../settings/store.js';
import { BRAND_NAME, createWordmark } from '../brand.js';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';
import { gearIcon, sourceIcon } from '../icons.js';
import { cleanNickInput, NickPersistence } from './nick.js';

export interface HomeDeps {
  /** Play pressed (button, or Enter in the nick field) with the current nick. */
  onPlay(nick: string): void;
  /** Gear button. The gear is shown only when this is given. */
  onSettings?(): void;
  /** The click sound hook (audio §3 row 7). */
  playClick?(source: UiClickSource): void;
  /** `ballio.nick` storage: undefined = the browser's localStorage, null = memory only. */
  readonly storage?: StorageLike | null;
  /** Touch device: touch hint lines and no autofocus. Default: `(pointer: coarse)` matches. */
  readonly touch?: boolean;
}

export interface HomeScreen {
  readonly el: HTMLElement;
  readonly visible: boolean;
  show(): void;
  hide(): void;
  focusNick(): void;
  /** The nick field's value. */
  nick(): string;
  /** Alive (Esc menu): Play resumes the same life. */
  setAlive(alive: boolean): void;
  /** Waiting for SPAWNED (or a money buy-in): spinner, clicks ignored. */
  setWaiting(waiting: boolean): void;
  /** Connecting: the "Connecting…" blocker covers the card, Play disabled. */
  setConnecting(connecting: boolean): void;
  /** No connection and no retry running (after a give-up dialog): Play disabled, no spinner. */
  setOffline(offline: boolean): void;
  /**
   * The server answered `full`; the next ask is in `retryInMs` (null clears it). Implies
   * connecting; `setConnecting(false)` clears it too.
   */
  setServerFull(retryInMs: number | null): void;
  /** The owner's wordmark art (null = the procedural placeholder). */
  setWordmark(img: AssetImage | null): void;
  /** `/api/info.sourceUrl` for the top-right source link ("" hides it). */
  setSourceUrl(url: string): void;
  /** "N players online" at the card's foot (`/api/info.online`; null hides it, space kept). */
  setOnline(count: number | null): void;
  /** Money rooms: the buy-in line under Play (null hides it); `test` adds the TEST MONEY tag. */
  setMoneyNote(text: string | null, test: boolean): void;
  /** Saves a pending nick change now. */
  flushNick(): void;
  /** Sets the nick field (the other menu front's value when switching fronts). */
  setNick?(value: string): void;
}

export interface PlayButtonState {
  readonly alive: boolean;
  readonly waiting: boolean;
  readonly connecting: boolean;
  readonly offline: boolean;
}

export interface PlayButtonView {
  readonly label: string;
  /** Spinner on the button. */
  readonly busy: boolean;
  /** Clicks are ignored. */
  readonly disabled: boolean;
}

/** The Play button for a state (ui-qol §3, §4.1: always labelled "Play"). DOM-free. */
export function playButtonView(s: PlayButtonState): PlayButtonView {
  const unavailable = s.connecting || s.offline;
  if (s.alive) return { label: t('menu.play'), busy: false, disabled: unavailable };
  return {
    label: t('menu.play'),
    busy: s.waiting && !unavailable,
    disabled: unavailable || s.waiting,
  };
}

export interface BlockerState {
  readonly connecting: boolean;
  /** When the next ask after a `full` answer is due (ms on `nowMs`'s clock), or null. */
  readonly fullRetryAtMs: number | null;
  readonly nowMs: number;
}

export interface BlockerView {
  readonly visible: boolean;
  /** Servers-full variant (title + countdown) rather than "Connecting…". */
  readonly full: boolean;
  readonly title: string;
  /** The countdown line (full only). */
  readonly detail: string | null;
}

/** The play card's blocker for a state (ui-qol §4.1 connecting state; servers full). DOM-free. */
export function blockerView(s: BlockerState): BlockerView {
  if (!s.connecting) return { visible: false, full: false, title: '', detail: null };
  if (s.fullRetryAtMs === null) {
    return { visible: true, full: false, title: t('menu.connecting'), detail: null };
  }
  const left = Math.ceil((s.fullRetryAtMs - s.nowMs) / 1000);
  return {
    visible: true,
    full: true,
    title: t('menu.full.title'),
    detail: left > 0 ? t('menu.full.retryIn', { s: left }) : t('menu.full.retrying'),
  };
}

/** The players-online line for `/api/info.online` ("" when unknown). DOM-free. */
export function onlineText(count: number | null): string {
  if (count === null || !Number.isFinite(count) || count < 0) return '';
  const n = Math.floor(count);
  return t(n === 1 ? 'menu.online.one' : 'menu.online.other', { n: n.toLocaleString('en-US') });
}

/** The two instruction lines under Play (ui-qol §4.1), as text with the key names marked. */
export function instructionLines(touch: boolean): [string, string] {
  return touch
    ? [t('menu.hint.touch.1'), t('menu.hint.touch.2')]
    : [t('menu.hint.desktop.1'), t('menu.hint.desktop.2')];
}

/** Splits a line at `{space}` / `{w}` so the key names can be bolded. */
export function keyParts(line: string): Array<{ readonly text: string; readonly key: boolean }> {
  const out: Array<{ text: string; key: boolean }> = [];
  const re = /\{(space|w)\}/g;
  let at = 0;
  for (let m = re.exec(line); m !== null; m = re.exec(line)) {
    if (m.index > at) out.push({ text: line.slice(at, m.index), key: false });
    out.push({ text: t(m[1] === 'space' ? 'menu.key.space' : 'menu.key.w'), key: true });
    at = m.index + m[0].length;
  }
  if (at < line.length) out.push({ text: line.slice(at), key: false });
  return out;
}

function isTouchDevice(): boolean {
  try {
    return globalThis.matchMedia?.('(pointer: coarse)').matches ?? false;
  } catch {
    return false;
  }
}

/** Waiting for SPAWNED gives up after this long (ui-flow §4.2). */
const WAIT_TIMEOUT_MS = NET_TIMING.playTimeoutSec * 1000;

export function createHome(deps: HomeDeps): HomeScreen {
  const touch = deps.touch ?? isTouchDevice();
  const persistence = new NickPersistence(deps.storage);
  const state = { alive: false, waiting: false, connecting: false, offline: false };
  /** Date.now() at which the next ask after a `full` answer is due, or null. */
  let fullRetryAtMs: number | null = null;
  let fullTimer: ReturnType<typeof setInterval> | null = null;
  let waitTimer: ReturnType<typeof setTimeout> | null = null;
  let visible = true;

  const wordmark = createWordmark();

  const nickInput = h('input', {
    id: 'nick',
    class: 'nick-field',
    type: 'text',
    name: 'nick',
    placeholder: t('menu.nick.placeholder'),
    autocomplete: 'off',
    autocapitalize: 'off',
    spellcheck: false,
    enterKeyHint: 'go',
    value: persistence.load(),
  });

  const playLabel = h('span', { class: 'btn-label' }, t('menu.play'));
  const playSpinner = h('span', { class: 'spinner', 'aria-hidden': 'true', hidden: true });
  const playBtn = h('button', { type: 'submit', class: 'btn-play' }, playSpinner, playLabel);
  const moneyText = h('span', { class: 'play-money-text' });
  const moneyTag = h('span', { class: 'money-tag', hidden: true }, t('money.test'));
  const moneyNote = h('p', { class: 'play-money', hidden: true }, moneyText, moneyTag);

  const form = h(
    'form',
    {
      class: 'play-form',
      novalidate: true,
      onSubmit: (ev: Event) => {
        ev.preventDefault();
        play();
      },
    },
    h('label', { class: 'sr-only', htmlFor: 'nick' }, t('menu.nick.label')),
    nickInput,
    playBtn,
  );

  const lines = instructionLines(touch).map((line) =>
    h(
      'p',
      { class: 'play-hint' },
      keyParts(line).map((p) => (p.key ? h('b', { class: 'play-key' }, p.text) : p.text)),
    ),
  );

  const gear = deps.onSettings
    ? h(
        'button',
        {
          type: 'button',
          class: 'card-icon play-gear',
          'aria-label': t('settings.title'),
          title: t('settings.title'),
          onClick: () => {
            deps.playClick?.('settings');
            deps.onSettings?.();
          },
        },
        gearIcon(),
      )
    : null;
  const source = h(
    'a',
    {
      class: 'card-icon play-source',
      href: '#',
      target: '_blank',
      rel: 'noopener noreferrer',
      'aria-label': t('footer.source'),
      title: t('footer.source'),
      hidden: true,
    },
    sourceIcon(),
  );

  const online = h('p', { class: 'play-online', 'aria-live': 'off' });
  online.style.visibility = 'hidden';

  const blockerText = h('p', { class: 'play-blocker-text' }, t('menu.connecting'));
  const blockerDetail = h('p', { class: 'play-blocker-detail', hidden: true });
  const blocker = h(
    'div',
    { class: 'play-blocker', role: 'status', hidden: true },
    blockerText,
    h('span', { class: 'ring-spinner', 'aria-hidden': 'true' }),
    blockerDetail,
  );

  const el = h(
    'section',
    { class: 'card play-card', 'aria-label': BRAND_NAME },
    gear,
    source,
    wordmark.el,
    form,
    moneyNote,
    h('div', { class: 'play-hints' }, lines),
    online,
    blocker,
  );

  function render(): void {
    const view = playButtonView(state);
    if (playLabel.textContent !== view.label) playLabel.textContent = view.label;
    playBtn.setAttribute('aria-disabled', view.disabled ? 'true' : 'false');
    playBtn.setAttribute('aria-busy', view.busy ? 'true' : 'false');
    playBtn.classList.toggle('is-busy', view.busy);
    playSpinner.hidden = !view.busy;
    const b = blockerView({ connecting: state.connecting, fullRetryAtMs, nowMs: Date.now() });
    blocker.hidden = !b.visible;
    blocker.classList.toggle('is-full', b.full);
    if (blockerText.textContent !== b.title && b.visible) blockerText.textContent = b.title;
    blockerDetail.hidden = b.detail === null;
    if (blockerDetail.textContent !== (b.detail ?? '')) blockerDetail.textContent = b.detail ?? '';
  }

  function clearFull(): void {
    fullRetryAtMs = null;
    if (fullTimer !== null) clearInterval(fullTimer);
    fullTimer = null;
  }

  function play(): void {
    const view = playButtonView(state);
    if (view.disabled) return;
    persistence.flush();
    deps.onPlay(nickInput.value);
  }

  nickInput.addEventListener('input', () => {
    const raw = nickInput.value;
    const cleaned = cleanNickInput(raw);
    if (cleaned !== raw) {
      // Keep the caret where it was, minus what was removed before it.
      const caret = nickInput.selectionStart ?? cleaned.length;
      const removed = raw.length - cleaned.length;
      nickInput.value = cleaned;
      const at = Math.max(0, Math.min(cleaned.length, caret - removed));
      try {
        nickInput.setSelectionRange(at, at);
      } catch {
        // Some input types do not support selection; text inputs do.
      }
    }
    persistence.change(nickInput.value);
  });

  function clearWaitTimer(): void {
    if (waitTimer !== null) clearTimeout(waitTimer);
    waitTimer = null;
  }

  function focusNick(): void {
    if (el.hidden || touch) return;
    nickInput.focus({ preventScroll: true });
    const end = nickInput.value.length;
    try {
      nickInput.setSelectionRange(end, end);
    } catch {
      // ignore
    }
  }

  render();

  return {
    el,
    get visible() {
      return visible;
    },
    show(): void {
      visible = true;
      show(el, true);
      focusNick();
    },
    hide(): void {
      visible = false;
      show(el, false);
    },
    focusNick,
    nick(): string {
      return nickInput.value;
    },
    setAlive(alive: boolean): void {
      state.alive = alive;
      if (alive) {
        state.waiting = false;
        clearWaitTimer();
      }
      render();
    },
    setWaiting(waiting: boolean): void {
      state.waiting = waiting;
      clearWaitTimer();
      if (waiting) {
        waitTimer = setTimeout(() => {
          waitTimer = null;
          state.waiting = false;
          render();
        }, WAIT_TIMEOUT_MS);
      }
      render();
    },
    setConnecting(connecting: boolean): void {
      state.connecting = connecting;
      if (connecting) {
        state.waiting = false;
        clearWaitTimer();
      } else {
        clearFull();
      }
      render();
    },
    setServerFull(retryInMs: number | null): void {
      clearFull();
      if (retryInMs !== null) {
        state.connecting = true;
        state.waiting = false;
        clearWaitTimer();
        fullRetryAtMs = Date.now() + Math.max(0, retryInMs);
        // The countdown line, a few times a second (cheap: text only changes once per second).
        fullTimer = setInterval(render, 250);
      }
      render();
    },
    setOffline(offline: boolean): void {
      state.offline = offline;
      render();
    },
    setWordmark(img: AssetImage | null): void {
      wordmark.setImage(img);
    },
    setSourceUrl(url: string): void {
      const ok = /^https?:\/\//i.test(url);
      source.hidden = !ok;
      if (ok) source.setAttribute('href', url);
    },
    setOnline(count: number | null): void {
      const text = onlineText(count);
      if (online.textContent !== text) online.textContent = text;
      online.style.visibility = text === '' ? 'hidden' : 'visible';
    },
    setMoneyNote(text: string | null, test: boolean): void {
      moneyNote.hidden = text === null;
      moneyText.textContent = text ?? '';
      moneyTag.hidden = !test || text === null;
    },
    flushNick(): void {
      persistence.flush();
    },
    setNick(value: string): void {
      const v = cleanNickInput(value);
      if (nickInput.value !== v) nickInput.value = v;
    },
  };
}
